// BlotterTab — Quotes + trades table with status grouping, row expansion,
// pricing breakdown, confirmation matching, settlement pipeline stepper.

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertTriangle, CheckCircle, XCircle, Search } from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { SETTLEMENT_STEPS, LP_TRADE_STATUS_LABELS, QUOTE_STATUS_LABELS } from '../../lib/lp/constants';
import { getPipelineIndex } from '../../lib/lp/tradeStateMachine';

const formatCurrency = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString?.() || '0';
};
const formatRate = (r) => r != null ? Number(r).toFixed(4) : '—';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

// Settlement Pipeline Stepper
const PipelineStepper = ({ currentStatus }) => {
  const currentIdx = getPipelineIndex(currentStatus);
  return (
    <div className="flex items-center gap-1 my-2">
      {SETTLEMENT_STEPS.map((step, idx) => {
        const isComplete = idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-mono ${
                isComplete ? 'bg-zinc-700 text-white' : 'bg-zinc-100 text-zinc-400'
              } ${isCurrent ? 'ring-2 ring-zinc-400' : ''}`}>
                {idx + 1}
              </div>
              <span className={`text-[8px] font-mono mt-0.5 ${isComplete ? 'text-zinc-700' : 'text-zinc-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < SETTLEMENT_STEPS.length - 1 && (
              <div className={`flex-1 h-px ${isComplete && idx < currentIdx ? 'bg-zinc-600' : 'bg-zinc-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const BlotterTab = ({ quotes = [], trades = [], counterparties = {} }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('trades'); // 'trades' | 'quotes'

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  // Group trades by status
  const activeTrades = trades.filter(t => !['csd_payment_confirmed', 'cancelled', 'failed'].includes(t.status));
  const completedTrades = trades.filter(t => ['csd_payment_confirmed', 'cancelled', 'failed'].includes(t.status));

  const filterItems = (items) => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      item.id?.toLowerCase().includes(q) ||
      item.currencyPair?.toLowerCase().includes(q) ||
      item.counterpartyId?.toLowerCase().includes(q) ||
      item.rfqId?.toLowerCase().includes(q)
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('trades')}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-none transition-colors ${
              viewMode === 'trades' ? 'bg-zinc-700 text-white border-zinc-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            Trades ({trades.length})
          </button>
          <button
            onClick={() => setViewMode('quotes')}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-none transition-colors ${
              viewMode === 'quotes' ? 'bg-zinc-700 text-white border-zinc-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            Quotes ({quotes.length})
          </button>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
          />
        </div>
      </div>

      {/* Trades View */}
      {viewMode === 'trades' && (
        <div className="bg-white border border-zinc-200 rounded-none">
          {/* Active Trades */}
          <div className="p-3 border-b border-zinc-100 bg-zinc-50">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              Active ({filterItems(activeTrades).length})
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="w-8" />
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Trade ID</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Side</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Rate</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Value Date</th>
                </tr>
              </thead>
              <tbody>
                {filterItems(activeTrades).map(trade => (
                  <React.Fragment key={trade.id}>
                    <tr
                      className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      onClick={() => toggleRow(trade.id)}
                    >
                      <td className="py-3 px-2">
                        {expandedRow === trade.id ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                      </td>
                      <td className="py-3 px-3 font-mono text-xs">{trade.id}</td>
                      <td className="py-3 px-3 font-mono text-xs">{trade.currencyPair}</td>
                      <td className="py-3 px-3">
                        <span className={`font-mono text-[10px] uppercase tracking-wider ${trade.side === 'buy' ? 'text-zinc-700' : 'text-zinc-500'}`}>
                          {trade.side}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(trade.amount)}</td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{formatRate(trade.rate)}</td>
                      <td className="py-3 px-3">
                        <span {...getStatusBadge(trade.status)}>
                          {LP_TRADE_STATUS_LABELS[trade.status] || trade.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-xs text-zinc-500">{trade.settlement?.valueDate || '—'}</td>
                    </tr>
                    {expandedRow === trade.id && (
                      <tr className="border-b border-zinc-100 bg-zinc-50">
                        <td colSpan={8} className="py-4 px-6">
                          {/* Settlement Pipeline */}
                          <div className="mb-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Settlement Pipeline</p>
                            <PipelineStepper currentStatus={trade.status} />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            {/* Pricing Breakdown */}
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Pricing</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-zinc-400">Rate</span><span className="font-mono">{formatRate(trade.rate)}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-400">Amount</span><span className="font-mono">{formatCurrency(trade.amount)}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-400">Counter</span><span className="font-mono">{trade.counterAmount?.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-400">Bank Deal#</span><span className="font-mono text-[10px]">{trade.bankDealNo}</span></div>
                              </div>
                            </div>

                            {/* Confirmation Matching */}
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Confirmation</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex items-center gap-2">
                                  {trade.confirmation?.bankConfirmed ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-zinc-300" />}
                                  <span>Bank confirmed</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {trade.confirmation?.venueConfirmed ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-zinc-300" />}
                                  <span>Venue confirmed</span>
                                </div>
                                {trade.confirmation?.mismatch && (
                                  <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-none">
                                    <div className="flex items-center gap-1 text-red-700 mb-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      <span className="font-mono text-[10px] uppercase">Mismatch</span>
                                    </div>
                                    {trade.confirmation.mismatchFields?.map(field => (
                                      <div key={field} className="text-[10px] text-red-600">
                                        {field}: Bank {trade.confirmation[`bank${field.charAt(0).toUpperCase() + field.slice(1)}`]} vs Venue {trade.confirmation[`venue${field.charAt(0).toUpperCase() + field.slice(1)}`]}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Settlement Details */}
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Settlement</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-zinc-400">Value Date</span><span className="font-mono">{trade.settlement?.valueDate}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-400">Correspondent</span><span className="font-mono text-[10px]">{trade.settlement?.correspondent}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-400">Pre-Fund</span><span className="font-mono">{trade.preFund ? 'Yes' : 'No'}</span></div>
                                {trade.settlement?.ssiChanged && (
                                  <div className="mt-1 p-1 bg-amber-50 border border-amber-200 rounded-none flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                                    <span className="text-[10px] text-amber-700">SSI Changed</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filterItems(activeTrades).length === 0 && (
              <div className="text-center py-8 text-zinc-400 text-xs">No active trades</div>
            )}
          </div>

          {/* Completed Trades */}
          {filterItems(completedTrades).length > 0 && (
            <>
              <div className="p-3 border-b border-t border-zinc-100 bg-zinc-50">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Completed ({filterItems(completedTrades).length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {filterItems(completedTrades).map(trade => (
                      <tr key={trade.id} className="border-b border-zinc-100 hover:bg-zinc-50 opacity-70">
                        <td className="py-2 px-3 font-mono text-xs w-8" />
                        <td className="py-2 px-3 font-mono text-xs">{trade.id}</td>
                        <td className="py-2 px-3 font-mono text-xs">{trade.currencyPair}</td>
                        <td className="py-2 px-3 font-mono text-[10px] uppercase">{trade.side}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{formatCurrency(trade.amount)}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{formatRate(trade.rate)}</td>
                        <td className="py-2 px-3"><span {...getStatusBadge(trade.status)}>{LP_TRADE_STATUS_LABELS[trade.status] || trade.status}</span></td>
                        <td className="py-2 px-3 text-xs text-zinc-500">{trade.settlement?.valueDate || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Quotes View */}
      {viewMode === 'quotes' && (
        <div className="bg-white border border-zinc-200 rounded-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Quote ID</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">RFQ</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Mid</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Margin</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Client Rate</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filterItems(quotes).map(quote => (
                  <tr key={quote.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-3 font-mono text-xs">{quote.id}</td>
                    <td className="py-3 px-3 font-mono text-xs">{quote.rfqId}</td>
                    <td className="py-3 px-3 font-mono text-xs">{quote.currencyPair}</td>
                    <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(quote.amount)}</td>
                    <td className="py-3 px-3 text-right font-mono text-xs">{formatRate(quote.midRate)}</td>
                    <td className="py-3 px-3 text-right font-mono text-xs">{quote.margin}</td>
                    <td className="py-3 px-3 text-right font-mono text-xs font-medium">{formatRate(quote.clientRate)}</td>
                    <td className="py-3 px-3">
                      <span {...getStatusBadge(quote.status)}>
                        {QUOTE_STATUS_LABELS[quote.status] || quote.status}
                      </span>
                      {quote.offPolicy && (
                        <span className="ml-1 px-1 py-0.5 text-[8px] font-mono bg-amber-100 text-amber-700 border border-amber-200 rounded-none">
                          OFF-POLICY
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-zinc-500">{formatDateTime(quote.submittedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filterItems(quotes).length === 0 && (
              <div className="text-center py-8 text-zinc-400 text-xs">
                <FileText className="w-6 h-6 mx-auto mb-2" />
                No quotes found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlotterTab;
